import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';

export function exportXlsx(headers, rows, sheetName, filename) {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

// Excel con el mismo estilo profesional (encabezado de empresa en color, totales, filtro
// automático, fila congelada) que usan los reportes PLE del backend — generado aquí mismo
// en el navegador con ExcelJS, reutilizando los datos que la pantalla ya tiene cargados.
export async function exportStyledXlsx({ empresa, titulo, periodoLabel, columns, rows, totals, filename, sheetName, landscape }) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'InsightSoft ERP';
  addStyledSheet(wb, { empresa, titulo, periodoLabel, columns, rows, totals, sheetName, landscape });
  const buf = await wb.xlsx.writeBuffer();
  const blobUrl = window.URL.createObjectURL(new Blob([buf], { type: 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = blobUrl; a.download = filename; a.click();
  window.URL.revokeObjectURL(blobUrl);
}

// Varias hojas (cada una con el mismo estilo de exportStyledXlsx) en UN solo archivo/descarga
// — evita que el navegador bloquee descargas múltiples cuando un reporte necesita más de una
// hoja (ej. Movimientos + Resumen por Auxiliar).
export async function exportMultiSheetXlsx({ sheets, filename }) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'InsightSoft ERP';
  for (const s of sheets) addStyledSheet(wb, s);
  const buf = await wb.xlsx.writeBuffer();
  const blobUrl = window.URL.createObjectURL(new Blob([buf], { type: 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = blobUrl; a.download = filename; a.click();
  window.URL.revokeObjectURL(blobUrl);
}

function addStyledSheet(wb, { empresa, titulo, periodoLabel, columns, rows, totals, sheetName, landscape }) {
  const ws = wb.addWorksheet(sheetName || 'Reporte', {
    pageSetup: { paperSize: 9, orientation: landscape ? 'landscape' : 'portrait', fitToPage: true, fitToWidth: 1 },
  });
  const ncols = columns.length;
  const half = Math.max(1, Math.floor(ncols / 2));

  ws.mergeCells(1, 1, 1, ncols);
  const r1 = ws.getRow(1); r1.height = 22;
  const c1 = r1.getCell(1);
  c1.value = empresa?.razon_social || 'EMPRESA';
  c1.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
  c1.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A3A5E' } };
  c1.alignment = { horizontal: 'center', vertical: 'middle' };

  ws.mergeCells(2, 1, 2, half);
  ws.mergeCells(2, half + 1, 2, ncols);
  const r2 = ws.getRow(2); r2.height = 18;
  const c2a = r2.getCell(1);
  c2a.value = `RUC: ${empresa?.ruc || ''}`;
  c2a.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
  c2a.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E6DA4' } };
  c2a.alignment = { horizontal: 'left', vertical: 'middle' };
  const c2b = r2.getCell(half + 1);
  c2b.value = titulo;
  c2b.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
  c2b.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E6DA4' } };
  c2b.alignment = { horizontal: 'right', vertical: 'middle' };

  ws.mergeCells(3, 1, 3, half);
  ws.mergeCells(3, half + 1, 3, ncols);
  const r3 = ws.getRow(3); r3.height = 16;
  const c3a = r3.getCell(1);
  c3a.value = `Período: ${periodoLabel}`;
  c3a.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FFFFFFFF' } };
  c3a.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E6DA4' } };
  c3a.alignment = { horizontal: 'left', vertical: 'middle' };
  const c3b = r3.getCell(half + 1);
  c3b.value = `Generado: ${new Date().toLocaleString('es-PE')}`;
  c3b.font = { name: 'Calibri', size: 9, color: { argb: 'FFFFFFFF' } };
  c3b.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E6DA4' } };
  c3b.alignment = { horizontal: 'right', vertical: 'middle' };

  const r4 = ws.getRow(4); r4.height = 28;
  columns.forEach((col, i) => {
    const cell = r4.getCell(i + 1);
    cell.value = col.header;
    cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E4D8C' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  });
  ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4, column: ncols } };
  ws.views = [{ state: 'frozen', xSplit: 0, ySplit: 4 }];

  rows.forEach((row, idx) => {
    const wr = ws.getRow(5 + idx);
    const isEven = idx % 2 === 0;
    columns.forEach((col, ci) => {
      const cell = wr.getCell(ci + 1);
      const val = row[col.key];
      cell.value = (val === undefined || val === null) ? '' : val;
      if (col.type === 'number') { cell.numFmt = '#,##0.00'; cell.alignment = { horizontal: 'right', vertical: 'middle' }; }
      else cell.alignment = { horizontal: 'left', vertical: 'middle' };
      cell.font = { name: 'Calibri', size: 9 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isEven ? 'FFEDF5FF' : 'FFFFFFFF' } };
      cell.border = { bottom: { style: 'hair', color: { argb: 'FFCCE0FF' } }, right: { style: 'hair', color: { argb: 'FFCCE0FF' } } };
    });
  });

  if (totals) {
    const lastRow = 5 + rows.length;
    const tr = ws.getRow(lastRow);
    columns.forEach((col, ci) => {
      const cell = tr.getCell(ci + 1);
      if (ci === 0) cell.value = 'TOTALES';
      else if (col.totKey && totals[col.totKey] !== undefined) {
        cell.value = Number(Number(totals[col.totKey]).toFixed(2));
        cell.numFmt = '#,##0.00';
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
      }
      cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF1A3A5E' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF8DC' } };
      cell.border = { top: { style: 'medium', color: { argb: 'FF1E4D8C' } }, bottom: { style: 'medium', color: { argb: 'FF1E4D8C' } } };
    });
  }

  columns.forEach((col, i) => { ws.getColumn(i + 1).width = col.width || 15; });
}
